import type { IssueSeverity } from '@/shared/constants/issue-status'

/** Per-severity resolution targets (days). */
export type ResolutionTargets = Record<IssueSeverity, number>

/**
 * Per-severity **resolution targets** (days) — the V2 spine for "overdue / due soon" labels. NOT an
 * "SLA" in the UI (user-facing wording is always "resolution target"). This is the FE default; the
 * live values come from `GET /config/issue-resolution-targets` (gis-view-backend-requests #3) via
 * `useResolutionTargets`, and are threaded into the functions below as the `targets` argument.
 */
export const RESOLUTION_TARGETS: ResolutionTargets = {
  critical: 7,
  non_critical: 30,
}

/** Days allowed before an open issue of this severity is overdue. */
export function resolutionTargetFor(severity: IssueSeverity, targets: ResolutionTargets = RESOLUTION_TARGETS): number {
  return targets[severity]
}

/** How close an OPEN issue of `ageDays` is to its severity's resolution target. */
export type ResolutionStatus = 'overdue' | 'due-soon' | 'on-track'

/**
 * `overdue` once age reaches the target; `due-soon` within the trailing 3-day window before it;
 * `on-track` otherwise. Mirrors the V2 mockup's `isOver` (age ≥ target) / `willBreach` (age ≥ target−3).
 * Callers pass age in whole days; only meaningful for open issues.
 */
export function resolutionStatus(
  severity: IssueSeverity,
  ageDays: number,
  targets: ResolutionTargets = RESOLUTION_TARGETS,
): ResolutionStatus {
  const target = targets[severity]
  if (ageDays >= target) {
    return 'overdue'
  }
  if (ageDays >= target - 3) {
    return 'due-soon'
  }
  return 'on-track'
}
